import { Button, Chip } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { Suspense, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Flex from "../../../../../../components/Flex";
import { NoDataAvailable } from "../../../../../../components/NoDataAvailable";
import CustomTitle from "../../../../../../components/app/CustomTitle";
import useDisclosure from "../../../../../../hooks/use-disclosure";
import {
  TaskOption,
  useTodaysTask,
} from "../../../../../../queries/collaboration/tasks/use-todays-task";
// import { useAppointmentsList } from "../../../../../../queries/use-get-appointments";
import lazyWithRetry from "../../../../../../utils/lazy-with-retry";
import { getXtenantId } from "../../../../../../utils/utils";
import BatchReassignTaskDrawer from "../../../collabration/tasks/BatchReassignTaskDrawer";
import BatchResolveDrawer from "../../../collabration/tasks/BatchResolveDrawer";
import { FallBackComp } from "../../../scheduling/appointment/encounter/content-section/IntakeFormData";

const DashBoardTodaysTasks = lazyWithRetry(
  () => import("./DashBoardTodaysTasks")
);
const Questionnairereview = lazyWithRetry(
  () => import("../dashboard-questionnaire-review/Questionnairereview")
);

export const DashBoardTodaysTask = () => {
  const { data: tasks, isPending } = useTodaysTask(getXtenantId());
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const url =
    "/app/provider/collaboration/tasks?activeTask=provider-centric-tasks";

  const tasksData = useMemo(() => tasks?.data as TaskOption, [tasks]);

  const resolveDisc = useDisclosure();
  const reassignDisc = useDisclosure();

  const handleResolve = (taskId: string) => resolveDisc.open([taskId]);
  const handleReassign = (taskId: string) => reassignDisc.open([taskId]);

  // ECRH-4715: drawers only invalidate the main task list — refresh today's tasks too
  const refreshTodaysTasks = () =>
    queryClient.invalidateQueries({ queryKey: ["todays-task-type-list"] });

  return (
    <Flex
      flexDirection={"column"}
      gap={1}
      height={"100%"}
      overflow="hidden"
      p={1}
    >
      <BatchResolveDrawer
        disclosureHost={resolveDisc}
        setSelectedTaskModel={refreshTodaysTasks}
      />
      <BatchReassignTaskDrawer
        disclosureHost={reassignDisc}
        setSelectedTaskModel={refreshTodaysTasks}
      />

      <Flex justifyContent="space-between" alignItems="center">
        <CustomTitle
          sx={{ fontWeight: 500, fontSize: "16px", lineHeight: "120%" }}
        >
          Today's Tasks
          {!!tasksData?.totalCount && (
            <Chip
              label={tasksData?.totalCount ?? 0}
              sx={{
                fontWeight: 600,
                fontSize: "11px",
                color: "black",
                backgroundColor: "#AEDAFF",
                height: "28px",
                minWidth: "28px",
                borderRadius: "50%",
                ml: "8px",
              }}
              size="small"
            />
          )}
        </CustomTitle>
        <Button
          sx={{
            cursor: "pointer",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
              transform: "translateY(-1px)",
            },
          }}
          onClick={() => navigate(url)}
        >
          View All
        </Button>
      </Flex>

      {tasksData?.tasks?.length > 0 || isPending ? (
        <Suspense fallback={<FallBackComp />}>
          <DashBoardTodaysTasks
            tasks={tasksData?.tasks}
            isLoading={isPending}
            onResolve={handleResolve}
            onReassign={handleReassign}
          />
        </Suspense>
      ) : (
        <NoDataAvailable message="No tasks available" />
      )}
    </Flex>
  );
};

export const DashboardQuestionnaireReviewSection = () => {
  return (
    <Flex flexDirection={"column"} gap={0.5}>
      <Suspense fallback={<FallBackComp />}>
        <Questionnairereview />
      </Suspense>
    </Flex>
  );
};
